import React from "react";
import { View, StyleSheet, Platform } from "react-native";
import { useNavigation } from "@react-navigation/native";
import type { NavigationProp } from "@react-navigation/native";
import type { RootStackParamList } from "../../../navigation/AppNavigator.web";
import { Course, useEvaluateCourseMutation } from "../../../graphql/generated/graphql";
import CourseEvaluationCard from "../../../components/adminDashboard/CourseEvaluationCard/CourseEvaluationCard";
import CustomText from "../../../components/common/general/CustomText/CustomText";
import { formatTimeAgo } from "../../../utils/date";
import useResponsiveScreen from "../../../hooks/useResponsiveScreen";

interface CourseCardItemProps {
  course: Course;
}

interface CourseEvaluationCardContainerProps {
  courses: Course[];
}

const CourseCardItem: React.FC<CourseCardItemProps> = ({ course }) => {
  const navigation = useNavigation<NavigationProp<RootStackParamList>>();
  const [evaluateCourse] = useEvaluateCourseMutation();

  // Find the latest completed evaluation from the evaluations array
  const latestCompletedEvaluation = course.evaluations
    ?.filter((evaluation) => evaluation.status === "completed" && evaluation.createdAt)
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];

  const evaluatedDate = latestCompletedEvaluation?.createdAt
    ? new Date(latestCompletedEvaluation.createdAt).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : undefined;

  const courseDate = course.startDate
    ? (() => {
        try {
          const date = new Date(course.startDate);
          if (isNaN(date.getTime())) return undefined;
          return date.toLocaleDateString("en-US", { month: "short", year: "numeric" });
        } catch {
          return undefined;
        }
      })()
    : undefined;
  const studentCount = course.enrolledStudents?.length || 0;

  const assignmentCount = course.modules?.reduce((total, module) => total + (module.assignments?.length || 0), 0) || 0;
  const formattedLastSynced = course.last_modified ? formatTimeAgo(course.last_modified) : undefined;

  const handleViewEvaluation = () => {
    navigation.navigate("Course Evaluation Details", { courseId: course.id });
  };

  const handleEvaluate = async () => {
    try {
      await evaluateCourse({ variables: { courseId: course.id } });
      // Optionally navigate to the evaluation detail view after starting
      navigation.navigate("Course Evaluation Details", { courseId: course.id });
    } catch (err) {
      console.log("Error starting evaluation", err);
    }
  };

  return (
    <CourseEvaluationCard
      title={course.name || ""}
      courseCode={course.course_code || ""}
      courseDate={courseDate}
      studentCount={studentCount}
      assignmentCount={assignmentCount}
      lastSynced={formattedLastSynced}
      evaluatedDate={evaluatedDate}
      onViewEvaluation={handleViewEvaluation}
      onEvaluate={handleEvaluate}
    />
  );
};

const CourseEvaluationCardContainer: React.FC<CourseEvaluationCardContainerProps> = ({ courses }) => {
  const { width } = useResponsiveScreen();
  const isSmallScreen = width <= 770;

  if (courses.length === 0) {
    return <CustomText text="No courses found" size="m" />;
  }

  const containerStyle = [
    styles.container,
    Platform.OS === "web" && {
      gridTemplateColumns: isSmallScreen ? "1fr" : "repeat(2, 1fr)",
    },
  ];

  return (
    <View style={containerStyle}>
      {courses.map((course) => (
        <View key={course.id} style={styles.cardWrapper}>
          <CourseCardItem course={course} />
        </View>
      ))}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    ...(Platform.OS === "web"
      ? ({
          display: "grid",
          gap: 20,
          width: "100%",
        } as any)
      : {
          flexDirection: "column",
          gap: 20,
        }),
  },
  cardWrapper: {
    ...(Platform.OS === "web"
      ? ({
          width: "100%",
        } as any)
      : {}),
  },
});

export default CourseEvaluationCardContainer;
